import React from 'react';
import pick from 'lodash/pick';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import connectToStores from 'alt-utils/lib/connectToStores';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import {RepLists} from 'components/coaching/landing-pages/RepLists';
import UserStore from 'stores/UserStore';
import {List} from 'immutable';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import withAdHocPlanDialog from 'components/coaching/withAdHocPlanDialog';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import confirm from 'utils/confirm';
import {ensureReviewMetadata} from 'components/StatsPage2.utils';

const endPlanWarning = 'Are you sure you want to end this coaching plan?';

class RepsListContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.endPlan = this.endPlan.bind(this);
    this.state = {filterByTeam: true};
  }

  static getStores() {
    return [DirectReportsStore, CoachingPlanStore, UserStore, CoachingReviewTypeStore];
  }

  static getPropsFromStores({coachId, managerId}) {
    // Filter out unscheduled plans from list
    const plans = CoachingPlanStore.getState().coachingPlans;
    const coachIdForFiltering = coachId || managerId;
    const filteredPlans =
      plans &&
      plans.filter(
        // TODO should we stop fetching plans where the user is just a review coach if we're
        //  filtering them out anyhow?
        plan => plan.coach.id === coachIdForFiltering && !plan.unscheduled()
      );
    const [, reviewTypes] = typesAndCategories(CoachingReviewTypeStore);

    return {
      ...DirectReportsStore.getState().toObject(),
      ...pick(CoachingPlanStore.getState(), [
        'areCoachingPlansLoading',
        'wasErrorLoadingCoachingPlans',
      ]),
      users: UserStore.getState().users,
      coachingPlans: filteredPlans || List(),
      reviewTypes,
      hasNoCallReviewType: reviewTypes.some(t => !t.includesCalls),
    };
  }

  componentDidMount() {
    const userIds = this.props.directReportsUserIds;
    if (userIds && !userIds.isEmpty()) {
      DirectReportsActions.getSkillBreakdown(userIds);
    }
    ensureReviewMetadata();
  }

  componentDidUpdate(prevProps) {
    //
    // Notify parent when this component changes so as it loads and
    // displays data, any necessary auto scrolling in the parent
    // can react.
    //
    this.props.autoScrollIfNecessary();
  }

  async endPlan(plan) {
    confirm(endPlanWarning).then(async () => {
      await CoachingPlanActions.endPlan(plan.id);
      this.props.refreshCoachingData();
    });
  }

  filterByTeam() {
    const {
      coachingPlans: coachingPlansAll,
      directReports: directReportsAll,
      selectedTeam,
      suppressToggle,
    } = this.props;
    if (!selectedTeam || !selectedTeam.id || suppressToggle) {
      return this.props;
    }
    const coachingPlans = coachingPlansAll.filter(plan => {
      const teamId =
        // according to the Kotlin side recipient will not be null but info can be
        plan.recipient.info && plan.recipient.info.teamId;
      return selectedTeam.id === teamId;
    });
    const directReports = directReportsAll.filter(rep => selectedTeam.id === rep.teamId);

    return {coachingPlans, directReports};
  }

  shouldSuppressToggle() {
    // if we were forced by the parent to suppress, suppress
    if (this.props.suppressToggle) {
      return true;
    }

    // if the "selected team" isnt `null` (implicit "all" team), make sure to show
    if (this.props.selectedTeam && this.props.selectedTeam.id !== null) {
      return false;
    }

    // if we're on the "all" team, it doesnt make sense to show the "filter" toggle
    // (which team?)
    if (this.props.selectedTeam && this.props.selectedTeam.id === null) {
      return true;
    }

    // if we havent loaded direct reports yet, dont show the toggle yet
    if (!this.props.directReports) {
      return true;
    }

    // suppress if they only manage one team
    return new Set(this.props.directReports.map(dr => dr.teamId)).size === 1;
  }

  render() {
    const {filterByTeam} = this.state;
    const {coachingPlans, directReports} = filterByTeam ? this.filterByTeam() : this.props;

    const {plansLimit} = this.props;
    const plans = plansLimit ? coachingPlans.take(plansLimit) : coachingPlans;

    const shouldSuppressToggle = this.shouldSuppressToggle();

    return (
      <RepLists
        {...this.props}
        suppressToggle={shouldSuppressToggle}
        filterByTeam={filterByTeam}
        coachingPlans={plans}
        directReports={directReports}
        toggleTeamFilter={() => this.setState({filterByTeam: !filterByTeam})}
        endPlan={this.endPlan}
      />
    );
  }
}

export default withAdHocPlanDialog(connectToStores(RepsListContainer));
